import React, { useEffect, useMemo } from 'react';
import {
  Tabs,
  TabsItem,
  Gutter,
  Layout,
  Suspense,
  useApi,
  Paragraph,
  Well,
  LayoutItem,
} from '@axiom/ui';
import {
  useLocation,
  useParams,
  useNavigate,
  useSearchParams,
} from 'react-router';
import { CandidateProfileStatusStateMachine } from '@axiom/utils';
import type { Candidate, Opportunity } from '@axiom/validation';

import { TalentProfileDetail } from '../TalentProfileDetail/TalentProfileDetail';
import { CandidateExperience } from '../CandidateExperience/CandidateExperience';
import { WindowUtil } from '../../utils/window-util';
import { TalentOpportunityTab } from '../TalentOpportunityTab/TalentOpportunityTab';
import { TaxonomyApi } from '../../api/taxonomy-api';
import { CandidateApi } from '../../api/candidate-api';
import { CandidateOpportunityLegacyApi } from '../../api/candidate-opportunity-legacy-api';
import { UserApi } from '../../api/user-api';
import { CandidateAvailabilityModalStore } from '../../stores/candidate-availability-modal-store';
import { ReputationDetails } from '../ReputationDetails/ReputationDetails';

import { TalentProfileWarnings } from './TalentProfileWarnings';
import { TalentProfileHeader } from './TalentProfileHeader';
import { TalentProfileActivityLog } from './TalentProfileActivityLog';

const noopFn = () => {};

enum TalentProfileTabName {
  PROFILE = 'profile',
  OPPORTUNITIES = 'opportunities',
  EXPERIENCE = 'experience',
  ACTIVITY_LOG = 'activity-log',
  FEEDBACK = 'feedback',
}

export type TalentProfileProps = {
  candidateId: Candidate['id'];
  opportunityId?: Opportunity['id'];
  isBenchCandidate?: boolean;
  expanded?: boolean;
  isDetail?: boolean;
  onCloseSidedrawer?: () => void;
  hideTitleBar?: boolean;
  hideWarnings?: boolean;
};

export const TalentProfile = ({
  candidateId,
  opportunityId,
  isBenchCandidate = false,
  expanded = false,
  isDetail = false,
  onCloseSidedrawer = noopFn,
  hideTitleBar = false,
  hideWarnings = false,
}: TalentProfileProps) => {
  const location = useLocation();
  const { id, viewId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [
    { data: taxonomy },
    { data: candidate },
    { data: opportunityCandidatesByTalent },
    { data: user },
  ] = useApi(
    TaxonomyApi.readTaxonomy(),
    CandidateApi.readCandidate(candidateId),
    CandidateOpportunityLegacyApi.readCandidateOpportunities(candidateId),
    UserApi.readSessionUser()
  );

  const showReputation = useMemo(() => {
    if (location.pathname.includes('opportunity-detail')) {
      return 'opportunity-detail';
    }
    return location.pathname.includes('talent-detail') ? 'talent-detail' : null;
  }, [location.pathname]);

  const rootPath = useMemo(() => {
    if (location.pathname.includes('talent-detail')) {
      return 'talent-detail';
    }
    if (location.pathname.includes('bench')) {
      return 'bench';
    }
    return location.pathname.includes('opportunity-detail')
      ? `opportunity-detail/${id}/talent`
      : 'talent';
  }, [location.pathname, id]);

  const paramId = location.pathname.includes('opportunity-detail')
    ? viewId
    : id;

  const basePath = `/${rootPath}/${paramId}`;

  const startTab = useMemo(() => {
    if (location.pathname.includes('/activitylog')) {
      return TalentProfileTabName.ACTIVITY_LOG;
    }
    if (location.pathname.includes('/experience')) {
      return TalentProfileTabName.EXPERIENCE;
    }
    if (location.pathname.includes('/opportunities')) {
      return TalentProfileTabName.OPPORTUNITIES;
    }
    return location.pathname.includes('/feedback') && showReputation
      ? TalentProfileTabName.FEEDBACK
      : TalentProfileTabName.PROFILE;
  }, [location.pathname, rootPath, opportunityId]);

  const goToTab = (tabSlug: string) => {
    void navigate(WindowUtil.appendQueryParams(`${basePath}/${tabSlug}`));
  };

  useEffect(() => {
    if (searchParams.get('openExperiences')) {
      void navigate('./experience?scrollToExceptionRequest=true', {
        replace: true,
      });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    const shouldSetTitle =
      expanded && !location.pathname.includes('opportunity-detail');
    if (shouldSetTitle) {
      WindowUtil.title(candidate.calculatedDisplayName ?? '');
    }
    return () => {
      if (shouldSetTitle) {
        WindowUtil.title('Axiom for HQ');
      }
    };
  }, [expanded, location.pathname, candidate.calculatedDisplayName]);

  const nextAvailableStatuses = useMemo(() => {
    const candidateProfileStatusTransition =
      new CandidateProfileStatusStateMachine(
        candidate,
        opportunityCandidatesByTalent
      );
    const statuses =
      candidateProfileStatusTransition.allowedEndUserStatusTransitions();
    return statuses.map((s: string) => ({
      label: s,
      value: s,
    }));
  }, [candidate, opportunityCandidatesByTalent]);

  if (!candidate.id) {
    return (
      <Gutter vertical="60px" horizontal="30px">
        <Paragraph name="no-talent-id-text">
          Please select a talent from the list.
        </Paragraph>
      </Gutter>
    );
  }

  const handleAvailabilityClick = (cid: Candidate['id']) => {
    void CandidateAvailabilityModalStore.openModal(cid);
  };

  const handleOpportunitiesClick = () => {
    goToTab('opportunities');
  };

  return (
    <Layout direction="vertical" stretched name="TALENT_PROFILE">
      <TalentProfileHeader
        nextAvailableStatuses={nextAvailableStatuses}
        isDetail={isDetail}
        onClickPopOut={onCloseSidedrawer}
        candidate={candidate}
        user={user}
        hideTitleBar={hideTitleBar}
        onAvailabilityClick={handleAvailabilityClick}
      />
      <Gutter bottom="16px" />
      {!hideWarnings && (
        <Gutter horizontal="16px">
          <Well name="talent-profile-warnings-well" background="light blue">
            <TalentProfileWarnings
              candidate={candidate}
              onAvailabilityClick={handleAvailabilityClick}
              onOpportunitiesClick={handleOpportunitiesClick}
            />
          </Well>
        </Gutter>
      )}
      <Tabs fluid scrollable stretched startTab={startTab}>
        <TabsItem
          label="Profile"
          name="profile"
          onClick={() => {
            goToTab('profile');
          }}
        >
          <Gutter horizontal="16px">
            <TalentProfileDetail
              name="PROFILE_CONTAINER"
              isDetail={isDetail}
              candidate={candidate}
              isEnvoySalesUser={user.roles.includes('EnvoySales')}
              taxonomy={taxonomy}
              opportunityCandidatesByTalent={opportunityCandidatesByTalent}
              opportunityId={opportunityId}
              isBenchCandidate={isBenchCandidate}
            />
          </Gutter>
        </TabsItem>
        <TabsItem
          label="Opportunities"
          name="opportunities"
          onClick={() => {
            goToTab('opportunities');
          }}
        >
          <Gutter horizontal="16px" vertical="8px">
            <Suspense>
              <TalentOpportunityTab candidate={candidate} />
            </Suspense>
          </Gutter>
        </TabsItem>
        <TabsItem
          label="Experience"
          name="experience"
          onClick={() => {
            goToTab('experience');
          }}
        >
          <Gutter horizontal="16px">
            <Suspense>
              <CandidateExperience candidate={candidate} />
            </Suspense>
          </Gutter>
        </TabsItem>
        {showReputation && candidate.id ? (
          <TabsItem
            label="Feedback"
            name="feedback"
            onClick={() => {
              goToTab('feedback');
            }}
          >
            <Layout stretched direction="vertical">
              <LayoutItem
                name="FEEDBACK_LAYOUT_ITEM"
                stretched
                topGutter="24px"
                bottomGutter={
                  showReputation === 'opportunity-detail' ? '0px' : '24px'
                }
                horizontalGutter={
                  showReputation === 'opportunity-detail' ? '0px' : '24px'
                }
              >
                <ReputationDetails
                  name="REPUTATION_DETAILS"
                  candidateId={candidate.id}
                  viewedIn={showReputation}
                />
              </LayoutItem>
            </Layout>
          </TabsItem>
        ) : null}
        <TabsItem
          label="Activity Log"
          name="activity-log"
          onClick={() => {
            goToTab('activitylog');
          }}
        >
          <Gutter horizontal="16px">
            <Suspense>
              <TalentProfileActivityLog
                name="TALENT_PROFILE_ACTIVITY_LOG"
                candidate={candidate}
                opportunities={opportunityCandidatesByTalent}
              />
            </Suspense>
          </Gutter>
        </TabsItem>
      </Tabs>
    </Layout>
  );
};
